import { Button } from '@crazyegginc/hatch';

import { PLAN_RANKS } from '/src/features/_global/constants';

import { ReactComponent as CrownIcon } from '@crazyegginc/hatch/dist/images/icon-crown-filled.svg';
import { ReactComponent as TickIcon } from '@crazyegginc/hatch/dist/images/icon-tick-basic.svg';

function PricingButton({ isCurrentPlan = false, label, ariaLabel, isSubmitting, onClick, isDowngrade = false }) {
  return (
    <Button
      aria-label={ariaLabel}
      variant="primary"
      className="w-full justify-center"
      disabled={isCurrentPlan || isSubmitting || isDowngrade}
      onClick={() => onClick()}
    >
      {isCurrentPlan ? 'Current Plan' : label}
    </Button>
  );
}

function PricingFeatureList({ ariaLabel, listFeatures }) {
  return (
    <ul
      aria-label={ariaLabel}
      className="[&:not(:last-of-type)]:border-b border-b-mystic-500 mt-6 pb-6 first-of-type:min-h-[236px] gap-3 flex flex-col"
    >
      {listFeatures.map((feature, index) => {
        return (
          <li key={`${index}-${feature}`} className="text-sm flex items-center">
            <TickIcon className=" text-dodger-blue-500 w-3.5 fill-current mr-4" aria-hidden="true" />
            <span dangerouslySetInnerHTML={{ __html: feature }} />
          </li>
        );
      })}
    </ul>
  );
}

function getCurrentSubscriptionState(currentPlan, subscription) {
  const currentPlanName = currentPlan?.toLowerCase?.() ?? null;
  const currentPeriod = subscription?.plan?.period?.toLowerCase?.() ?? null;
  const currentPrice = subscription?.plan?.price ?? null;

  return { currentPlanName, currentPeriod, currentPrice };
}

function getCandidatePriceAtCurrentInterval(plan, currentPeriod) {
  return currentPeriod && plan[currentPeriod]?.price != null ? plan[currentPeriod].price : null;
}

function isKnownPlanDowngrade(planName, currentPlanName) {
  if (!(planName in PLAN_RANKS) || !(currentPlanName in PLAN_RANKS)) return null;
  return PLAN_RANKS[planName] < PLAN_RANKS[currentPlanName];
}

function getGeneralCardState(plan, selectedInterval, currentPlan, subscription, onSubscribe) {
  const isFreePlan = plan.name === 'free';
  const intervalData = selectedInterval === 'monthly' ? plan.monthly : plan.yearly;
  const { currentPlanName, currentPeriod } = getCurrentSubscriptionState(currentPlan, subscription);
  const isCurrentPlanInterval = isFreePlan || (currentPlanName === plan.name && currentPeriod === selectedInterval);

  const isDowngrade = isKnownPlanDowngrade(plan.name, currentPlanName) ?? false;

  const isCurrentPlan = isCurrentPlanInterval || (currentPlanName === plan.name && currentPeriod == null);
  const ctaLabel = `${isDowngrade ? 'Downgrade' : 'Upgrade'} to ${plan.label}`;

  return {
    intervalData,
    priceSuffix: selectedInterval === 'monthly' ? '/mo' : '/yr',
    isCurrentPlan,
    isButtonDisabled: false,
    ctaLabel,
    showCrownIcon: !isFreePlan,
    onSubscribeClick: () => onSubscribe({ id: intervalData?.stripeId }),
  };
}

function getSemrushCardState(plan, currentPlan, subscription) {
  const intervalData = plan.monthly;
  const semrushPlanId = intervalData?.semrushId;
  const { currentPlanName, currentPeriod, currentPrice } = getCurrentSubscriptionState(currentPlan, subscription);
  const candidatePriceAtCurrentInterval = getCandidatePriceAtCurrentInterval(plan, currentPeriod);

  const isDowngrade = candidatePriceAtCurrentInterval < currentPrice;
  const isCurrentPlan = currentPlanName === plan.name && currentPeriod == null;
  const ctaLabel = `${isDowngrade ? 'Contact Semrush for downgrade' : 'Upgrade'} to ${plan.label}`;

  return {
    intervalData,
    priceSuffix: '/mo',
    isCurrentPlan,
    isButtonDisabled: isDowngrade || !semrushPlanId,
    ctaLabel,
    showCrownIcon: true,
    onSubscribeClick: () => window.SM?.client?.('requestInAppPurchase', [semrushPlanId]),
  };
}

function PlanCard({ plan, isSubmitting, cardState }) {
  const { intervalData, priceSuffix, isCurrentPlan, isButtonDisabled, ctaLabel, showCrownIcon, onSubscribeClick } =
    cardState;
  const featureSectionId = `${plan.name}-features`;

  return (
    <li className="font-sans text-black-pearl-500 border border-mystic-500 bg-white rounded-lg p-6">
      <article className="flex flex-col h-full">
        <header>
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-xl">{plan.label}</h2>
            {showCrownIcon ? <CrownIcon className="text-[#fbbd33] h-5 w-5 fill-current" aria-hidden="true" /> : null}
          </div>
          <p className="text-4xl mb-8 leading-none">
            <strong>${intervalData?.price?.toLocaleString() ?? 0}</strong>
            <span className="font-normal text-xs ml-1">{priceSuffix}</span>
          </p>
          <PricingButton
            isCurrentPlan={isCurrentPlan}
            label={ctaLabel}
            ariaLabel={`${ctaLabel} — top`}
            onClick={onSubscribeClick}
            isSubmitting={isSubmitting}
            isDowngrade={isButtonDisabled}
          />
        </header>
        <section aria-labelledby={featureSectionId}>
          <h3 id={featureSectionId} className="sr-only">
            {plan.label} plan features
          </h3>
          <PricingFeatureList listFeatures={plan.top_features} ariaLabel="Core features" />

          {plan.bottom_features?.length ? (
            <PricingFeatureList listFeatures={plan.bottom_features} ariaLabel="Additional features" />
          ) : null}
        </section>

        <footer className="mt-auto">
          <PricingButton
            isCurrentPlan={isCurrentPlan}
            label={ctaLabel}
            ariaLabel={`${ctaLabel} — bottom`}
            onClick={onSubscribeClick}
            isSubmitting={isSubmitting}
            isDowngrade={isButtonDisabled}
          />
        </footer>
      </article>
    </li>
  );
}

export function PricingCard({
  plan,
  selectedInterval,
  currentPlan,
  subscription,
  isSubmitting,
  onSubscribe,
  isSemrush,
}) {
  const cardState = isSemrush
    ? getSemrushCardState(plan, currentPlan, subscription)
    : getGeneralCardState(plan, selectedInterval, currentPlan, subscription, onSubscribe);

  return <PlanCard plan={plan} isSubmitting={isSubmitting} cardState={cardState} />;
}
